import { CBQQuestionType } from "../../../../api/Quiz/InitCBQQuestion";
import { sortCbq } from "../../../../utils/cbqOrder";

// Draft shapes for the live-authoring builder. Each entity is created on the
// server as soon as it's added (init endpoints), so drafts carry the returned
// `serverId` alongside the client-only `tempId` (used as the stable React key).
// `serverId` is undefined only for the brief window before its init resolves.

export interface DraftQuestion {
  tempId: string;
  serverId?: string;
  type: CBQQuestionType;
  prompt: string;
  helpText: string;
  optionsJson: Record<string, unknown>;
  correctAnswerJson: Record<string, unknown>;
  points: number;
}

export interface DraftScenario {
  tempId: string;
  serverId?: string;
  title: string;
  estimatedMinutes: string; // kept as string for the input; parsed before patch
  pdfS3Key: string; // already uploaded to S3
  questions: DraftQuestion[];
}

export interface DraftCBQExam {
  // Set once the draft exam is created on the server (first scenario added).
  examId?: string;
  title: string;
  description: string;
  mode: string;
  // PROD_WITH_MCQ only: the existing MCQ quiz that gates this CBQ exam and the
  // score (%) needed to pass it. requiredQuizScore kept as string for the input.
  quizId: string;
  requiredQuizScore: string;
  scenarios: DraftScenario[];
}

export const CBQ_MODE_OPTIONS = [
  { id: "PROD_WITH_MCQ", name: "Production (with MCQ)" },
  { id: "STANDALONE_TRAINING", name: "Standalone Training" },
];

const tempId = () => Math.random().toString(36).slice(2);

export const makeDraftQuestion = (type: CBQQuestionType): DraftQuestion => ({
  tempId: tempId(),
  type,
  prompt: "",
  helpText: "",
  optionsJson: {},
  correctAnswerJson: {},
  points: 1,
});

export const makeDraftScenario = (): DraftScenario => ({
  tempId: tempId(),
  title: "",
  estimatedMinutes: "",
  pdfS3Key: "",
  questions: [],
});

export const makeDraftExam = (): DraftCBQExam => ({
  title: "",
  description: "",
  mode: CBQ_MODE_OPTIONS[0].id,
  quizId: "",
  requiredQuizScore: "",
  scenarios: [],
});

// ----- Hydration (edit mode) -----

// Server rows carry `sortKey` (and `order` once published); the builder relies
// on array position instead, so ordering is resolved once here with the shared
// comparator the student side uses.

const str = (v: unknown) =>
  v === null || v === undefined ? "" : String(v);

/**
 * Map an existing server-side exam into the builder's draft shape.
 *
 * Every entity keeps its server id as `serverId`, so the builder's patch /
 * delete / reorder paths work on a hydrated draft exactly as they do on one it
 * created itself. `tempId`s are freshly minted — they are React keys only and
 * never reach the API.
 *
 * Pure: no fetching, no side effects.
 */
export const hydrateDraft = (
  exam: HydratableExam,
  scenarios: HydratableScenario[],
  questionsByScenario: Record<string, HydratableQuestion[]>
): DraftCBQExam => ({
  examId: exam.id,
  title: str(exam.title),
  description: str(exam.description),
  mode: exam.mode || CBQ_MODE_OPTIONS[0].id,
  quizId: str(exam.quizId),
  requiredQuizScore: str(exam.requiredQuizScore),
  scenarios: sortCbq(scenarios).map((s) => ({
    tempId: tempId(),
    serverId: s.id,
    title: str(s.title),
    estimatedMinutes: str(s.estimatedMinutes),
    pdfS3Key: str(s.pdfS3Key),
    questions: sortCbq(questionsByScenario[s.id] ?? []).map((q) => ({
        tempId: tempId(),
        serverId: q.id,
        type: q.type,
        prompt: str(q.prompt),
        helpText: str(q.helpText),
        optionsJson: q.optionsJson ?? {},
        correctAnswerJson: q.correctAnswerJson ?? {},
        // A question always carries points in the editor; the server may not
        // have one yet on a freshly-initialised row.
        points: typeof q.points === "number" ? q.points : 1,
      })),
  })),
});

// Structural inputs to `hydrateDraft`, kept minimal so the API DTOs can gain
// fields without touching this module.
export interface HydratableExam {
  id: string;
  title?: string | null;
  description?: string | null;
  mode?: string | null;
  quizId?: string | null;
  requiredQuizScore?: number | null;
}

export interface HydratableScenario {
  id: string;
  // Authoritative position; `order` is only backfilled at publish time.
  sortKey?: string | null;
  order?: number | null;
  title?: string | null;
  estimatedMinutes?: number | null;
  pdfS3Key?: string | null;
}

export interface HydratableQuestion {
  id: string;
  sortKey?: string | null;
  order?: number | null;
  type: CBQQuestionType;
  prompt?: string | null;
  helpText?: string | null;
  optionsJson?: Record<string, unknown> | null;
  correctAnswerJson?: Record<string, unknown> | null;
  points?: number | null;
}
